import { AxiosResponse } from 'axios';

import { BorrowerDto } from 'prequalificationV2/api/BorrowersApi';
import { HttpClient } from 'api/HttpClient';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';
import ResponseError from 'errors/common.errors';

import { PrequalificationNextStep } from './preQualificationApi.enums';
import {
  CheckActiveApplicationsResponse,
  CreateBorrowerIntentRequest,
  CreateBorrowerIntentResponse,
  CompleteBorrowerRegistration,
  CompleteBorrowerRegistrationResponseDto,
  GetBorrowerShortInfoBySsnRequestDto,
  GetBorrowerShortInfoBySsnResponseDto,
  GetBorrowerShortInfoByEmailRequestDto,
  GetBorrowerShortInfoByEmailResponseDto,
  GetCoborrowerShortInfoBySsnRequestDto,
  GetCoborrowerShortInfoBySsnResponseDto,
  GetIntermediaryVerificationStatusRequest,
  GetIntermediaryVerificationStatusResponse,
  GetPhoneNumberLineTypeRequest,
  GetPhoneNumberLineTypeResponse,
  UpdateBorrowerMarketingConsentRequest,
  UpdateBorrowerMarketingConsentResponse,
  GetCompaniesResponseDto,
  GetIntermediariesSuggestionsDto,
  IntermediarySuggestionDto,
  CreateBorrowerRequest,
  RunApplicationUnderwritingResponseDto,
  RunApplicationUnderwritingDto,
  UpdatePropertyInformationRequestDto,
  PropertyInformationResponseDto,
  RunProductAndOffersDistributionResponseDto,
  UnderwritingCoborrowerDto,
} from './preQualificationApi.types';

export class PreQualificationApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL + '/prequalificationV2');
  }

  public async getPhoneNumberLineType(body: GetPhoneNumberLineTypeRequest) {
    const response = await this.instance
      .post(`/phoneNumber-line-type`, body)
      .then((res: AxiosResponse<GetPhoneNumberLineTypeResponse | ErrorDto>) => res.data);

    if ((response as ErrorDto)?.error?.errorCode === ErrorCodes.GET_USER_ELIGIBILITY_FAILED) {
      throw new ResponseError(response as ErrorDto);
    }

    return response as GetPhoneNumberLineTypeResponse;
  }

  public async getIntermediaryVerificationStatus({ id }: GetIntermediaryVerificationStatusRequest) {
    return this.instance
      .get(`/intermediaries/${encodeURIComponent(id)}/verification-status`)
      .then((response: AxiosResponse<GetIntermediaryVerificationStatusResponse>) => response.data);
  }

  public async getIntermediarySuggestions({ name }: GetIntermediariesSuggestionsDto) {
    return this.instance
      .get(`/intermediaries?name=${encodeURIComponent(name)}`)
      .then((response: AxiosResponse<IntermediarySuggestionDto[]>) => response.data);
  }

  public async checkActiveApplications() {
    return this.instance
      .get(`/applications/active-status`)
      .then((response: AxiosResponse<CheckActiveApplicationsResponse>) => response.data);
  }

  public async getBorrowerShortInfoBySsn(getBorrowerShortInfoBySsnRequestDto: GetBorrowerShortInfoBySsnRequestDto) {
    return this.instance
      .post(`/borrowers/short-info/by-ssn`, getBorrowerShortInfoBySsnRequestDto)
      .then((response: AxiosResponse<GetBorrowerShortInfoBySsnResponseDto>) => response.data);
  }

  public async getBorrowerShortInfoByEmail(
    getBorrowerShortInfoByEmailRequestDto: GetBorrowerShortInfoByEmailRequestDto,
  ) {
    return this.instance
      .post(`/borrowers/short-info/by-email`, getBorrowerShortInfoByEmailRequestDto)
      .then((response: AxiosResponse<GetBorrowerShortInfoByEmailResponseDto>) => response.data);
  }

  public async getCoborrowerShortInfoBySsn(
    getCoborrowerShortInfoBySsnRequestDto: GetCoborrowerShortInfoBySsnRequestDto,
  ) {
    return this.instance
      .post(`/coborrowers/short-info/by-ssn`, getCoborrowerShortInfoBySsnRequestDto)
      .then((response: AxiosResponse<GetCoborrowerShortInfoBySsnResponseDto>) => response.data);
  }

  public async createBorrowerIntent(body: CreateBorrowerIntentRequest) {
    return this.instance
      .post(`/borrowers/intents`, body)
      .then((response: AxiosResponse<CreateBorrowerIntentResponse>) => response.data);
  }

  public async updateBorrowerMarketingConsent(body: UpdateBorrowerMarketingConsentRequest) {
    return this.instance
      .put(`/borrowers/marketing-consent`, body)
      .then((response: AxiosResponse<UpdateBorrowerMarketingConsentResponse | ErrorDto>) => response.data);
  }

  public async createBorrower(createBorrower: CreateBorrowerRequest) {
    return this.instance
      .post('/coborrowers', createBorrower)
      .then((response: AxiosResponse<BorrowerDto | ErrorDto>) => {
        return response.data;
      });
  }

  public async completeBorrowerRegistration(createBorrower: CompleteBorrowerRegistration) {
    return this.instance
      .put('/borrowers/complete', createBorrower)
      .then((response: AxiosResponse<CompleteBorrowerRegistrationResponseDto>) => {
        return response.data;
      });
  }

  public async getNextStep(): Promise<{ step: PrequalificationNextStep }> {
    return this.instance
      .get('/next-step')
      .then((response: AxiosResponse<{ step: PrequalificationNextStep }>) => response.data);
  }

  public async getCompanies(name: string) {
    return this.instance
      .get(`/employer-companies?name=${encodeURIComponent(name)}`)
      .then((response: AxiosResponse<GetCompaniesResponseDto>) => response.data);
  }

  public async runUnderwriting(runUnderwriting: RunApplicationUnderwritingDto) {
    return this.instance
      .post('/underwriting', runUnderwriting)
      .then((response: AxiosResponse<RunApplicationUnderwritingResponseDto>) => {
        return response.data;
      });
  }

  public async getPropertyInformation(applicationId: string): Promise<PropertyInformationResponseDto> {
    return this.instance.get(`/${applicationId}/property-information`).then((response) => response.data);
  }

  public async updatePropertyInformation(
    updatePropertyInformationRequestDto: UpdatePropertyInformationRequestDto,
  ): Promise<void> {
    return this.instance
      .put(`/property-information`, updatePropertyInformationRequestDto)
      .then((response) => response.data);
  }

  public async runProductAndOffersDistribution(
    applicationId: string,
  ): Promise<RunProductAndOffersDistributionResponseDto> {
    return this.instance.post(`/${applicationId}/offer-distribution`).then((response) => response.data);
  }

  public async addCoborrower(applicationId: string, coborrowerData: UnderwritingCoborrowerDto) {
    return this.instance
      .post(`/${applicationId}/coborrower`, coborrowerData)
      .then((response: AxiosResponse<RunApplicationUnderwritingResponseDto>) => response.data);
  }

  public async skipCoborrower(applicationId: string) {
    return this.instance
      .post(`/${applicationId}/coborrower/skip`)
      .then((response: AxiosResponse<RunApplicationUnderwritingResponseDto>) => response.data);
  }
}

export const preQualificationApi = new PreQualificationApi();
